import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const RouterContext = createContext(null);

function currentPath() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return path;
}

export function Router({ children }) {
  const [path, setPath] = useState(currentPath);

  useEffect(() => {
    const onPop = () => setPath(currentPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [path]);

  const navigate = useCallback((to) => {
    const [rawPath, hash] = to.split('#');
    const next = (rawPath || '/').replace(/\/+$/, '') || '/';
    const url = hash ? `${next}#${hash}` : next;
    window.history.pushState({}, '', url);
    setPath(next);
    if (hash) {
      requestAnimationFrame(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.scrollTo(0, 0);
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <RouterContext.Provider value={{ path, navigate }}>
      {children}
    </RouterContext.Provider>
  );
}

export function useRouter() {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error('useRouter must be used within Router');
  return ctx;
}

export function Link({ to, className, children, onClick, ...rest }) {
  const { path, navigate } = useRouter();
  const dest = (to.split('#')[0] || '/').replace(/\/+$/, '') || '/';
  const active = path === dest;

  return (
    <a
      href={to}
      className={className}
      aria-current={active ? 'page' : undefined}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        onClick?.(e);
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}
